import React, { useEffect, useState } from 'react'

import { createPortal } from 'react-dom'
import { FiDownload } from 'react-icons/fi'
import { HiOutlineCog, HiOutlineRefresh, HiOutlineX } from 'react-icons/hi'
import { ImSpinner2 } from 'react-icons/im'

import ErrorBoundary from '@/insights/common/ErrorBoundary'

interface InsightFullscreenProps {
  title: string
  caption: React.ReactNode
  content: React.ReactNode
  footer: React.ReactNode
  error: string
  onClose: () => void
  onConfigure: () => void
  onRefresh: () => void
  onExportCsv?: () => void
  csvExportLoading?: boolean
}

const InsightFullscreenContent: React.FC<InsightFullscreenProps> = ({
  title,
  caption,
  content,
  footer,
  error,
  onClose,
  onConfigure,
  onRefresh,
  onExportCsv,
  csvExportLoading = false,
}) => {
  const footerIconSize = 17

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 bg-base-100 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-base-200 shrink-0">
        <div className="flex flex-col min-w-0">
          <h3 className="text-primary font-bold text-lg">{title.toUpperCase()}</h3>
          {caption && <div className="text-sm text-base-content font-light">{caption}</div>}
        </div>
        <div className="flex items-center gap-1">
          <div className="tooltip tooltip-bottom" data-tip="Settings">
            <button onClick={onConfigure} className="btn btn-ghost p-2 h-fit">
              <HiOutlineCog size={20} />
            </button>
          </div>
          <div className="tooltip tooltip-bottom" data-tip="Exit Fullscreen">
            <button onClick={onClose} className="btn btn-ghost p-2 h-fit">
              <HiOutlineX size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {error && (
          <div className="w-full items-center">
            <div className="alert alert-error text-white mr-6 ml-6 mt-2">
              <strong>Error - </strong> {error}
            </div>
          </div>
        )}
        <ErrorBoundary>{!error && <div className="flex w-full flex-col">{content}</div>}</ErrorBoundary>
      </div>

      {/* Footer */}
      <footer className="flex px-6 py-2 justify-between w-full items-center shrink-0 border-t border-base-200">
        <div className="w-full text-sm text-base-content/70 font-light">{footer || ''}</div>
        <div className="flex gap-2">
          {onExportCsv && (
            <div className="tooltip w-fit" data-tip={csvExportLoading ? 'Exporting CSV...' : 'Export CSV'}>
              <button
                className={`btn btn-ghost p-1 h-fit ${csvExportLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                onClick={csvExportLoading ? undefined : onExportCsv}
                disabled={csvExportLoading}
              >
                {csvExportLoading ? (
                  <ImSpinner2 size={footerIconSize} className="animate-spin" />
                ) : (
                  <FiDownload size={footerIconSize} />
                )}
              </button>
            </div>
          )}
          <div className="tooltip w-fit tooltip-left" data-tip="Refresh Data">
            <button className="btn btn-ghost cursor-pointer p-1 h-fit" onClick={() => onRefresh()}>
              <HiOutlineRefresh size={footerIconSize} />
            </button>
          </div>
        </div>
      </footer>
    </div>
  )
}

const InsightFullscreen: React.FC<InsightFullscreenProps> = (props) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  if (!mounted) return null

  return createPortal(<InsightFullscreenContent {...props} />, document.body)
}

export default InsightFullscreen
